import React, { useState, useEffect } from "react";
import { fetchPatientList } from "../../../api";
import { FaUserFriends } from "react-icons/fa";
import { FaUserCheck } from "react-icons/fa";
import { FaUserClock } from "react-icons/fa";
import { FaSortDown, FaSortUp } from "react-icons/fa";
import { IoSearchOutline } from "react-icons/io5";
import "./PatientsList.css";
import AuthenticatedImage from "../../AuthenticatedImage";

const PatientsList = ({ selectedPatientId, onPatientClick }) => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [sortDirection, setSortDirection] = useState(null);

  const loadPatients = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchPatientList();
      setPatients(response.patients || []);
    } catch (error) {
      console.error("Error loading patients:", error);
      setError("Failed to load participants");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPatients();
  }, []);

  // Simplified handlePatientClick - only pass the patient object
  const handlePatientClick = (patient) => {
    if (onPatientClick) {
      onPatientClick(patient);
    }
  };

  // ...rest of the existing code remains the same...
  const getAllPatients = () => {
    return patients || [];
  };

  const sortPatientsAlphabetically = (patientsToSort, direction) => {
    return [...patientsToSort].sort((firstPatient, secondPatient) =>
      direction === "asc"
        ? String(firstPatient.patient_name || "").localeCompare(
            String(secondPatient.patient_name || ""),
            undefined,
            { sensitivity: "base" }
          )
        : String(secondPatient.patient_name || "").localeCompare(
            String(firstPatient.patient_name || ""),
            undefined,
            { sensitivity: "base" }
          )
    );
  };

  const handlePatientNameSort = () => {
    setSortDirection((currentDirection) =>
      currentDirection === "asc" ? "desc" : "asc"
    );
  };

  const getFilteredPatients = () => {
    let filteredPatients = [...getAllPatients()];

    switch (activeTab) {
      case "active":
        filteredPatients = patients.filter(
          (patient) => patient.number_of_videos > 0
        );
        break;
      case "inactive":
        filteredPatients = patients.filter(
          (patient) => patient.number_of_videos === 0
        );
        break;
      default:
        filteredPatients = patients;
    }

    if (searchInput) {
      filteredPatients = filteredPatients.filter(
        (patient) =>
          patient.patient_name
            .toLowerCase()
            .includes(searchInput.toLowerCase()) ||
          patient.patient_id.toLowerCase().includes(searchInput.toLowerCase())
      );
    }

    return sortDirection
      ? sortPatientsAlphabetically(filteredPatients, sortDirection)
      : filteredPatients;
  };

  const getFilteredCounts = () => {
    const allPatients = patients || [];
    const activePatients = patients.filter(
      (patient) => patient.number_of_videos > 0
    );
    const inactivePatients = patients.filter(
      (patient) => patient.number_of_videos === 0
    );

    const filteredAll = searchInput
      ? allPatients.filter(
          (patient) =>
            patient.patient_name
              .toLowerCase()
              .includes(searchInput.toLowerCase()) ||
            patient.patient_id.toLowerCase().includes(searchInput.toLowerCase())
        )
      : allPatients;

    const filteredActive = searchInput
      ? activePatients.filter(
          (patient) =>
            patient.patient_name
              .toLowerCase()
              .includes(searchInput.toLowerCase()) ||
            patient.patient_id.toLowerCase().includes(searchInput.toLowerCase())
        )
      : activePatients;

    const filteredInactive = searchInput
      ? inactivePatients.filter(
          (patient) =>
            patient.patient_name
              .toLowerCase()
              .includes(searchInput.toLowerCase()) ||
            patient.patient_id.toLowerCase().includes(searchInput.toLowerCase())
        )
      : inactivePatients;

    return {
      total: filteredAll.length,
      active: filteredActive.length,
      inactive: filteredInactive.length,
    };
  };

  const getTotalForCurrentView = () => {
    const filteredCounts = getFilteredCounts();

    switch (activeTab) {
      case "active":
        return filteredCounts.active;
      case "inactive":
        return filteredCounts.inactive;
      default:
        return filteredCounts.total;
    }
  };

  const getPatientStatus = (patient) => {
    return patient.number_of_videos > 0 ? "Active" : "Inactive";
  };

  const getStatusButtonClass = (status) => {
    return status === "Active"
      ? "status-button active"
      : "status-button inactive";
  };

  if (loading) {
    return (
      <div className="patients-container">
        <div className="loading-message">Loading participants...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="patients-container">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={loadPatients}>Retry</button>
        </div>
      </div>
    );
  }

  const filteredPatients = getFilteredPatients();
  const filteredCounts = getFilteredCounts();
  const totalForCurrentView = getTotalForCurrentView();

  return (
    <div className="patients-container">
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-icon-wrapper first">
            <FaUserFriends className="stat-icon" />
          </div>
          <div className="stat-content">
            <span className="stat-label">
              Total Participants {searchInput && "(filtered)"}
            </span>
            <span className="stat-value">{filteredCounts.total}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper second">
            <FaUserCheck className="stat-icon" />
          </div>
          <div className="stat-content">
            <span className="stat-label">
              Active Participants {searchInput && "(filtered)"}
            </span>
            <span className="stat-value">{filteredCounts.active}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper third">
            <FaUserClock className="stat-icon" />
          </div>
          <div className="stat-content">
            <span className="stat-label">
              Inactive Participants {searchInput && "(filtered)"}
            </span>
            <span className="stat-value">{filteredCounts.inactive}</span>
          </div>
        </div>
      </div>

      <div className="patients-table-container">
        <div className="table-header">
          <div className="tab-section">
            <button
              className={`tab-button ${activeTab === "all" ? "active" : ""}`}
              onClick={() => setActiveTab("all")}
            >
              All Participants
            </button>
            <button
              className={`tab-button ${activeTab === "active" ? "active" : ""}`}
              onClick={() => setActiveTab("active")}
            >
              Active Participants
            </button>
            <button
              className={`tab-button ${
                activeTab === "inactive" ? "active" : ""
              }`}
              onClick={() => setActiveTab("inactive")}
            >
              Inactive Participants
            </button>
          </div>

          <div className="table-controls">
            <div className="search-container">
              <IoSearchOutline className="search-icon" />
              <input
                placeholder="Search participants..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
        </div>

        <div className="patients-table">
          <div className="table-header-row">
            <span className="header-cell">Profile Picture</span>
            <span className="header-cell">
              <button
                className="sort-button"
                onClick={handlePatientNameSort}
                aria-label={`Sort participants by name${
                  sortDirection === "asc"
                    ? ", currently ascending"
                    : sortDirection === "desc"
                    ? ", currently descending"
                    : ""
                }`}
              >
                Participant Name
                {sortDirection === "asc" && <FaSortUp />}
                {sortDirection === "desc" && <FaSortDown />}
              </button>
            </span>
            <span className="header-cell">Videos</span>
            <span className="header-cell">Status</span>
            <span className="header-cell">Actions</span>
          </div>

          {filteredPatients.map((patient, index) => (
            <div key={patient.patient_id} className="table-row">
              <span className="table-cell">
                  <AuthenticatedImage
                    patientId={patient.patient_id}
                    alt={patient.patient_name}
                    circle={true}
                    size={32}
                    borderWidth={1}
                    borderColor="#dee2e6"
                    className="patient-avatar"
                  />
              </span>
              <span className="table-cell patient-name">
                {patient.patient_name}
              </span>
              <span className="table-cell">{patient.number_of_videos}</span>
              <span className="table-cell">
                <button
                  className={getStatusButtonClass(getPatientStatus(patient))}
                >
                  {getPatientStatus(patient)}
                </button>
              </span>
              <span className="table-cell">
                <button
                  className="action-button"
                  onClick={() => handlePatientClick(patient)}
                >
                  View
                </button>
              </span>
            </div>
          ))}

          {filteredPatients.length === 0 && (
            <div className="no-data-row">
              <span className="no-data-message">
                {searchInput
                  ? "No participants found matching your search."
                  : "No participants available."}
              </span>
            </div>
          )}
        </div>

        <div className="table-footer">
          <span className="table-info">
            Showing {filteredPatients.length} of {totalForCurrentView} participants
          </span>
        </div>
      </div>
    </div>
  );
};

export default PatientsList;